import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { History, RotateCcw, ChevronDown, ChevronUp, Clock, User, FileText } from 'lucide-react';

export default function CatalogHistoryPanel({ catalogId, open, onClose, onRollback }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedSnapshot, setExpandedSnapshot] = useState(null);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rolling, setRolling] = useState(false);

  const fetchVersions = useCallback(async () => {
    if (!catalogId || !open) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/catalogs/${catalogId}/versions`);
      setVersions(data);
    } catch { toast.error('Failed to load version history'); }
    setLoading(false);
  }, [catalogId, open]);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  const toggleExpand = async (versionId) => {
    if (expandedId === versionId) {
      setExpandedId(null);
      setExpandedSnapshot(null);
      return;
    }
    try {
      const { data } = await api.get(`/catalogs/${catalogId}/versions/${versionId}`);
      setExpandedId(versionId);
      setExpandedSnapshot(data.snapshot);
    } catch { toast.error('Failed to load version details'); }
  };

  const handleRollback = async () => {
    if (!rollbackTarget) return;
    setRolling(true);
    try {
      await api.post(`/catalogs/${catalogId}/versions/${rollbackTarget.id}/rollback`);
      toast.success('Catalog rolled back successfully');
      setRollbackTarget(null);
      onRollback?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rollback failed'); }
    setRolling(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getRelativeTime = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 bg-white rounded-sm border-[#E5E0D8] overflow-hidden" data-testid="catalog-history-panel">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E5E0D8]">
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <History size={20} className="text-[#C5A059]" />Version History
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-12"><div className="spinner-gold" /></div>
              ) : versions.length === 0 ? (
                <div className="text-center py-12">
                  <History size={36} className="mx-auto text-[#E5E0D8] mb-3" />
                  <p className="text-sm text-[#5C5C5C]">No version history yet</p>
                  <p className="text-xs text-[#5C5C5C] mt-1">Versions are created each time the catalog is saved</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-4 bottom-4 w-px bg-[#E5E0D8]" />

                  <div className="space-y-1">
                    {versions.map((v, idx) => (
                      <div key={v.id} className="relative pl-10" data-testid={`version-${v.id}`}>
                        {/* Timeline dot */}
                        <div className={`absolute left-[10px] top-5 w-[11px] h-[11px] rounded-full border-2 ${idx === 0 ? 'bg-[#C5A059] border-[#C5A059]' : 'bg-white border-[#E5E0D8]'}`} />

                        <div className={`p-4 rounded-sm border transition-colors ${expandedId === v.id ? 'border-[#C5A059] bg-[#FDFBF7]' : 'border-[#E5E0D8] hover:bg-[#F5F2EB]'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpand(v.id)}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-[#1A1A1A]">{formatDate(v.created_at)}</span>
                                <Badge className="bg-[#F2E8D5] text-[#C5A059] text-[9px] px-1.5">{getRelativeTime(v.created_at)}</Badge>
                                {idx === 0 && <Badge className="bg-[#2D6A4F] text-white text-[9px] px-1.5">Latest</Badge>}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-[#5C5C5C] mb-1.5">
                                <User size={10} /><span>{v.updated_by || 'System'}</span>
                              </div>
                              <p className="text-xs text-[#5C5C5C] leading-relaxed">{v.change_summary}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-3 shrink-0">
                              <Button size="sm" variant="outline" onClick={() => setRollbackTarget(v)} className="rounded-sm border-[#E5E0D8] text-[10px] h-7 px-2" data-testid={`rollback-btn-${v.id}`}>
                                <RotateCcw size={12} className="mr-1" />Rollback
                              </Button>
                              <button onClick={() => toggleExpand(v.id)} className="p-1 text-[#5C5C5C] hover:text-[#C5A059]">
                                {expandedId === v.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          </div>

                          {/* Expanded details */}
                          {expandedId === v.id && expandedSnapshot && (
                            <div className="mt-3 pt-3 border-t border-[#E5E0D8]" data-testid={`version-details-${v.id}`}>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C5C] font-semibold">Title</span>
                                  <p className="text-[#1A1A1A] mt-0.5">{expandedSnapshot.title}</p>
                                </div>
                                <div>
                                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C5C] font-semibold">Status</span>
                                  <p className="text-[#1A1A1A] mt-0.5 capitalize">{expandedSnapshot.status}</p>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C5C] font-semibold">Sections</span>
                                  <div className="mt-1 flex flex-wrap gap-1.5">
                                    {expandedSnapshot.sections?.map((s, i) => (
                                      <Badge key={s.id || `snap-section-${i}-${s.name}`} className="bg-[#F5F2EB] text-[#5C5C5C] text-[10px] font-normal">
                                        <FileText size={10} className="mr-1" />{s.name} ({s.products?.length || 0})
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                                {expandedSnapshot.branding?.company_name && (
                                  <div>
                                    <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C5C] font-semibold">Branding</span>
                                    <p className="text-[#1A1A1A] mt-0.5">{expandedSnapshot.branding.company_name}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation */}
      <Dialog open={!!rollbackTarget} onOpenChange={() => setRollbackTarget(null)}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]" data-testid="rollback-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <RotateCcw size={18} className="text-[#C5A059]" />Confirm Rollback
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#5C5C5C] mb-3">
              This will restore the catalog to the state from:
            </p>
            {rollbackTarget && (
              <div className="p-3 bg-[#F5F2EB] rounded-sm border border-[#E5E0D8]">
                <p className="text-sm font-medium text-[#1A1A1A]">{formatDate(rollbackTarget.created_at)}</p>
                <p className="text-xs text-[#5C5C5C] mt-1">{rollbackTarget.change_summary}</p>
                <p className="text-[10px] text-[#5C5C5C] mt-1">By {rollbackTarget.updated_by}</p>
              </div>
            )}
            <p className="text-xs text-[#5C5C5C] mt-3">
              The current state will be saved as a new version before rolling back.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
            <Button onClick={handleRollback} disabled={rolling} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-rollback-btn">
              {rolling ? 'Rolling back...' : 'Rollback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
